import { useMemo } from "react";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";

import styles from "./Answer.module.css";
import { ChatAppResponse, getCitationFilePath } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { SpeechOutputBrowser } from "./SpeechOutputBrowser";
import { SpeechOutputAzure } from "./SpeechOutputAzure";

interface Props {
    answer: ChatAppResponse;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    showSpeechOutputBrowser?: boolean;
    showSpeechOutputAzure?: boolean;
    speechUrl: string | null;
}

export const Answer = ({
    answer,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    showSpeechOutputAzure,
    showSpeechOutputBrowser,
    speechUrl
}: Props) => {
    // Hard-coded aircraft details with inline styles
    // const aircraftDetails = (
    //     <div className={styles.aircraftDetails}>
    //         <div style={{ color: 'red' }}>AeroWing</div>
    //         <div style={{ color: 'blue' }}>AB720</div>
    //         <div style={{ color: 'green', whiteSpace: 'nowrap' }}>Liberty Airlines</div>
    //         <div style={{ color: '#9713ce' }}>N8855A</div>
    //     </div>
    // );

    const followupQuestions = answer.context?.followup_questions || [];
    const messageContent = answer.message.content;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, onCitationClicked), [answer]);
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
        <Stack.Item>
            <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: 8 }}>
                <AnswerIcon />
                <div>
                    {showSpeechOutputAzure && <SpeechOutputAzure url={speechUrl} />}
                    {showSpeechOutputBrowser && <SpeechOutputBrowser answer={sanitizedAnswerHtml} />}
                </div>
            </Stack>
        </Stack.Item>

        <Stack.Item grow>
            <div className={styles.answerContentContainer}>
                <div className={styles.answerText} dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}></div>
                {/* {aircraftDetails} */}
            </div>
        </Stack.Item>

        {!!parsedAnswer.citations.length && (
            <Stack.Item>
                <Stack horizontal wrap tokens={{ childrenGap: 4 }}> {/* Reduced spacing here */}
                    <span className={styles.citationLearnMore}>References:</span>
                    {parsedAnswer.citations.map((x, i) => {
                        const path = getCitationFilePath(x);
                        const citationNameWithoutExtension = x.replace(/\.[^/.]+$/, ""); // Remove the file extension
                        return (
                            <a key={i} className={styles.citation} title={x} onClick={() => onCitationClicked(path)}>
                                {`${++i}. ${citationNameWithoutExtension}`}
                            </a>
                        );
                    })}
                </Stack>
            </Stack.Item>
        )}

        {followupQuestions.length && onFollowupQuestionClicked && (
            <Stack.Item>
                <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 4 }}> {/* Reduced spacing here */}
                    <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                    {followupQuestions.map((x, i) => {
                        return (
                            <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                {`${x}`}
                            </a>
                        );
                    })}
                </Stack>
            </Stack.Item>
        )}
    </Stack>
);
};


